import { Terminal } from 'xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import { getSocket, isConnected } from '@/websocket/wsClient'

export interface ManagedTerminal {
  sessionId: string
  terminal:  Terminal
  fitAddon:  FitAddon
}

// Full record stored internally; only the public ManagedTerminal subset is returned to callers.
interface SessionRecord extends ManagedTerminal {
  resizeObserver: ResizeObserver
  // Named handlers stored so socket.off() can remove them precisely on dispose.
  // Anonymous listeners would leak — every createTerminal call would add a new
  // listener that could never be removed.
  onOutput:  (data: { sessionId: string; data: string }) => void
  onBlocked: (data: { reason: string }) => void
  onError:   (msg: unknown) => void
  onExit:    (data: { sessionId: string; exitCode: number }) => void
}

const sessions = new Map<string, SessionRecord>()

// The session ID that most recently sent terminal:input. Used to route
// terminal:blocked to the correct terminal: the server emits it per-socket
// without a sessionId, so we track which session the user was typing in.
let lastInputSessionId: string | null = null

/**
 * Create an xterm.js terminal, attach it to `element`, and open a PTY session
 * on the server via WebSocket. Returns the public terminal handle.
 */
export function createTerminal(element: HTMLElement, sessionId: string, cwd: string): ManagedTerminal {
  const terminal = new Terminal({
    theme:       { background: '#27353f', foreground: '#cdd6f4', cursor: '#0fdb63' },
    fontSize:    13,
    fontFamily:  'JetBrains Mono, Fira Code, monospace',
    cursorStyle: 'block',
    scrollback:  5000,
  })

  const fitAddon      = new FitAddon()
  const webLinksAddon = new WebLinksAddon()

  terminal.loadAddon(fitAddon)
  terminal.loadAddon(webLinksAddon)
  terminal.open(element)
  try { fitAddon.fit() } catch { /* element may not yet be in the layout */ }

  const socket = getSocket()
  socket.emit('terminal:create', { sessionId, cwd })

  // ── Named socket event handlers ───────────────────────────────────────────────

  const onOutput = (data: { sessionId: string; data: string }) => {
    if (data.sessionId === sessionId) terminal.write(data.data)
  }

  // terminal:blocked has no sessionId in the payload (server spec). Route to the
  // terminal that most recently sent input — that is always the one the user was
  // typing in, which is the session that triggered the block.
  const onBlocked = (data: { reason: string }) => {
    if (lastInputSessionId === sessionId) {
      terminal.writeln(`\r\n\x1b[31m[blocked] ${data.reason}\x1b[0m`)
    }
  }

  // terminal:error is per-socket with no sessionId — show in every open terminal
  // so the message is never silently dropped. In practice this fires on CWD
  // rejection (visible in the new terminal) or malformed payloads (rare).
  const onError = (msg: unknown) => {
    const text = typeof msg === 'string'
      ? msg
      : (msg as Record<string, unknown>)?.message as string | undefined ?? String(msg)
    terminal.writeln(`\r\n\x1b[31m[error] ${text}\x1b[0m`)
  }

  const onExit = (data: { sessionId: string; exitCode: number }) => {
    if (data.sessionId !== sessionId) return
    terminal.writeln(`\r\n\x1b[90m[process exited with code ${data.exitCode}]\x1b[0m`)
    // Remove the dead record so dispose is a no-op if called after exit.
    sessions.delete(sessionId)
  }

  socket.on('terminal:output',  onOutput)
  socket.on('terminal:blocked', onBlocked)
  socket.on('terminal:error',   onError)
  socket.on('terminal:exit',    onExit)

  // ── Forward user input and resize events ──────────────────────────────────────

  terminal.onData(input => {
    lastInputSessionId = sessionId
    socket.emit('terminal:input', { sessionId, input })
  })

  terminal.onResize(({ cols, rows }) => {
    socket.emit('terminal:resize', { sessionId, cols, rows })
  })

  // ── Auto-refit on container resize ────────────────────────────────────────────
  // Handles SplitPane drag and any other layout changes. Uses the container
  // element (not the window) so hidden terminals don't trigger unnecessary fits.

  const resizeObserver = new ResizeObserver(() => {
    try { fitAddon.fit() } catch { /* ignore if element is detached */ }
  })
  resizeObserver.observe(element)

  const record: SessionRecord = {
    sessionId, terminal, fitAddon, resizeObserver,
    onOutput, onBlocked, onError, onExit,
  }
  sessions.set(sessionId, record)

  return { sessionId, terminal, fitAddon }
}

/**
 * Dispose a terminal session:
 *   1. Emit terminal:destroy so the server kills the PTY and frees the session ID.
 *   2. Remove all socket listeners for this session.
 *   3. Disconnect the ResizeObserver.
 *   4. Release the xterm.js instance.
 *
 * Freeing the session ID on the server matters because TerminalPanel uses v-if —
 * when the panel is hidden and reshown, the same session IDs from terminalStore
 * are re-sent in terminal:create. Without destroy, the server rejects them as
 * duplicates and the terminals open blank with an error.
 */
export function disposeTerminal(sessionId: string): void {
  const record = sessions.get(sessionId)
  if (!record) return

  if (isConnected()) {
    getSocket().emit('terminal:destroy', { sessionId })
  }

  const socket = getSocket()
  socket.off('terminal:output',  record.onOutput)
  socket.off('terminal:blocked', record.onBlocked)
  socket.off('terminal:error',   record.onError)
  socket.off('terminal:exit',    record.onExit)

  record.resizeObserver.disconnect()
  record.terminal.dispose()
  sessions.delete(sessionId)
}

/** Refit all open terminals. Useful after a programmatic layout change. */
export function refitAll(): void {
  for (const { fitAddon } of sessions.values()) {
    try { fitAddon.fit() } catch { /* ignore if terminal is detached */ }
  }
}
